import { useState, useEffect } from "react";
import axios from "axios";
import { Modal, Button, Form } from "react-bootstrap";

export default function ContestList() {
  const [contests, setContests] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    startDate: "",
    startTime: "",
    submissionEndType: "days", // 'days' or 'datetime'
    submissionEndDays: "4",
    submissionEndDate: "",
    submissionEndTime: "",
    contestEndType: "days", // 'days' or 'datetime'
    contestEndDays: "7",
    contestEndDate: "",
    contestEndTime: "",
    image: null,
  });

  // State for editing an existing contest
  const [editModal, setEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState(null);

  useEffect(() => {
    fetchContests();
  }, []);

  const fetchContests = async () => {
    try {
      const response = await axios.get("/api/contest/get_contests.php");
      setContests(response.data);
    } catch (error) {
      console.error("Error fetching contests:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  // Helper function to calculate end date when using "days after start"
  const calculateEndDate = (startDate, startTime, days) => {
    if (!startDate || !startTime) return "";
    const date = new Date(`${startDate}T${startTime}`);
    date.setDate(date.getDate() + parseInt(days));
    return date;
  };

  // Create new contest
  const handleCreateContest = async () => {
    const {
      title,
      description,
      startDate,
      startTime,
      submissionEndType,
      submissionEndDays,
      submissionEndDate,
      submissionEndTime,
      contestEndType,
      contestEndDays,
      contestEndDate,
      contestEndTime,
      image,
    } = formData;

    if (!title.trim() || !description.trim() || !startDate || !startTime) return;

    const formPayload = new FormData();
    formPayload.append("title", title);
    formPayload.append("description", description);
    formPayload.append("start_date", `${startDate} ${startTime}`);

    // Calculate submission end date
    const submissionEnd =
      submissionEndType === "days"
        ? calculateEndDate(startDate, startTime, submissionEndDays)
        : new Date(`${submissionEndDate}T${submissionEndTime}`);

    // Calculate contest end date
    const contestEnd =
      contestEndType === "days"
        ? calculateEndDate(startDate, startTime, contestEndDays)
        : new Date(`${contestEndDate}T${contestEndTime}`);

    formPayload.append("submission_end_date", submissionEnd.toISOString());
    formPayload.append("contest_end_date", contestEnd.toISOString());
    if (image) formPayload.append("image", image);

    try {
      await axios.post("/api/contest/create_contest.php", formPayload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      fetchContests();
      setShowModal(false);
      setFormData({
        title: "",
        description: "",
        startDate: "",
        startTime: "",
        submissionEndType: "days",
        submissionEndDays: "7",
        submissionEndDate: "",
        submissionEndTime: "",
        contestEndType: "days",
        contestEndDays: "14",
        contestEndDate: "",
        contestEndTime: "",
        image: null,
      });
    } catch (error) {
      console.error("Error creating contest:", error);
    }
  };

  // Utility functions to format Unix timestamps for date and time inputs
  const formatDate = (timestamp) => {
    const d = new Date(timestamp * 1000);
    return d.toISOString().split("T")[0];
  };

  const formatTime = (timestamp) => {
    const d = new Date(timestamp * 1000);
    return d.toTimeString().slice(0, 5);
  };

  // Calculate contest phase based on current time and contest dates
  const getContestPhase = (contest) => {
    const now = Date.now() / 1000; // current time in seconds
    if (now < contest.start_date) {
      return "Upcoming";
    } else if (now < contest.submissions_end_date) {
      return "Submissions Open";
    } else if (now < contest.end_date) {
      return "Voting Open";
    } else {
      return "Ended";
    }
  };

  // Opens the edit modal with pre-populated contest data
  const handleEditContest = (contest) => {
    setEditFormData({
      id: contest.id,
      title: contest.title,
      description: contest.description,
      startDate: formatDate(contest.start_date),
      startTime: formatTime(contest.start_date),
      submissionEndDate: formatDate(contest.submissions_end_date),
      submissionEndTime: formatTime(contest.submissions_end_date),
      contestEndDate: formatDate(contest.end_date),
      contestEndTime: formatTime(contest.end_date),
      // Include additional fields (like image) if needed
    });
    setEditModal(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value, files } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  // Update contest data
  const handleUpdateContest = async () => {
    if (!editFormData) return;

    const {
      id,
      title,
      description,
      startDate,
      startTime,
      submissionEndDate,
      submissionEndTime,
      contestEndDate,
      contestEndTime,
    } = editFormData;

    if (!title.trim() || !description.trim() || !startDate || !startTime) return;

    const formPayload = new FormData();
    formPayload.append("id", id);
    formPayload.append("title", title);
    formPayload.append("description", description);
    formPayload.append("start_date", `${startDate} ${startTime}`);
    formPayload.append("submission_end_date", `${submissionEndDate} ${submissionEndTime}`);
    formPayload.append("contest_end_date", `${contestEndDate} ${contestEndTime}`);
    // Append image if update supports it

    try {
      await axios.post("/api/contest/update_contest.php", formPayload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      fetchContests();
      setEditModal(false);
      setEditFormData(null);
    } catch (error) {
      console.error("Error updating contest:", error);
    }
  };

  // Delete a contest after confirmation
  const handleDeleteContest = async (contestId, e) => {
    e.stopPropagation(); // Prevent card navigation
    if (!window.confirm("Are you sure you want to delete this contest?")) return;

    try {
      await axios.post("/api/contest/delete_contest.php", { id: contestId });
      fetchContests();
    } catch (error) {
      console.error("Error deleting contest:", error);
    }
  };

  return (
    <div className="container py-4 px-md-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Contests</h2>
        <Button variant="primary" onClick={() => setShowModal(true)}>
          <i className="bi bi-plus-lg me-2"></i>
          Start New Contest
        </Button>
      </div>

      <div className="list-group">
        {contests.map((contest) => (
          <div
            key={contest.id}
            className="list-group-item list-group-item-action"
            style={{ cursor: "pointer" }}
            onClick={() => (window.location.href = `/contests/${contest.id}/`)}
          >
            <div className="row align-items-center">
              {/* Left image column */}
              <div className="col-md-2 d-flex align-items-center justify-content-center">
                <img
                  src={
                    contest.image_url ||
                    `https://www.hearthcards.net/images/contest/sketch/${contest.id}.svg`
                  }
                  alt={contest.title}
                  className="img-fluid rounded"
                  style={{ maxHeight: "100px", objectFit: "cover" }}
                />
              </div>
              {/* Right content column */}
              <div className="col-md-10">
                <div className="d-flex w-100 justify-content-between align-items-center">
                  <h5 className="mb-2">
                    {contest.title}{" "}
                    <span className="badge bg-primary">{getContestPhase(contest)}</span>
                  </h5>
                  <div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditContest(contest);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={(e) => handleDeleteContest(contest.id, e)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
                <p className="mb-2 text-start">{contest.description}</p>
                <div className="row text-muted small">
                  <div className="col-4 mb-1">
                    <i className="bi bi-calendar-event me-1"></i>
                    Starts: {new Date(contest.start_date * 1000).toLocaleString()}
                  </div>
                  <div className="col-4 mb-1">
                    <i className="bi bi-calendar-check me-1"></i>
                    Submissions End:{" "}
                    {new Date(contest.submissions_end_date * 1000).toLocaleString()}
                  </div>
                  <div className="col-4 mb-1">
                    <i className="bi bi-calendar-x me-1"></i>
                    Contest Ends: {new Date(contest.end_date * 1000).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create Contest Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New Contest</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Contest Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter contest title"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                placeholder="Enter contest description"
              />
            </Form.Group>

            <div className="row mb-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Start Time</Form.Label>
                  <Form.Control
                    type="time"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="mb-3">
              <Form.Label>Submissions End / Voting Starts</Form.Label>
              <div className="mb-2">
                <Form.Check
                  inline
                  type="radio"
                  name="submissionEndType"
                  label="Days after start"
                  value="days"
                  checked={formData.submissionEndType === "days"}
                  onChange={handleInputChange}
                />
                <Form.Check
                  inline
                  type="radio"
                  name="submissionEndType"
                  label="Specific date/time"
                  value="datetime"
                  checked={formData.submissionEndType === "datetime"}
                  onChange={handleInputChange}
                />
              </div>

              {formData.submissionEndType === "days" ? (
                <Form.Group>
                  <Form.Control
                    type="number"
                    name="submissionEndDays"
                    value={formData.submissionEndDays}
                    onChange={handleInputChange}
                    min="1"
                  />
                  <Form.Text className="text-muted">
                    Number of days after start date
                  </Form.Text>
                </Form.Group>
              ) : (
                <div className="row">
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Control
                        type="date"
                        name="submissionEndDate"
                        value={formData.submissionEndDate}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Control
                        type="time"
                        name="submissionEndTime"
                        value={formData.submissionEndTime}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-3">
              <Form.Label>Contest End</Form.Label>
              <div className="mb-2">
                <Form.Check
                  inline
                  type="radio"
                  name="contestEndType"
                  label="Days after start"
                  value="days"
                  checked={formData.contestEndType === "days"}
                  onChange={handleInputChange}
                />
                <Form.Check
                  inline
                  type="radio"
                  name="contestEndType"
                  label="Specific date/time"
                  value="datetime"
                  checked={formData.contestEndType === "datetime"}
                  onChange={handleInputChange}
                />
              </div>

              {formData.contestEndType === "days" ? (
                <Form.Group>
                  <Form.Control
                    type="number"
                    name="contestEndDays"
                    value={formData.contestEndDays}
                    onChange={handleInputChange}
                    min="1"
                  />
                  <Form.Text className="text-muted">
                    Number of days after start date
                  </Form.Text>
                </Form.Group>
              ) : (
                <div className="row">
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Contest End Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="contestEndDate"
                        value={formData.contestEndDate}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Contest End Time</Form.Label>
                      <Form.Control
                        type="time"
                        name="contestEndTime"
                        value={formData.contestEndTime}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </div>
                </div>
              )}
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Contest Image</Form.Label>
              <Form.Control
                type="file"
                name="image"
                onChange={handleInputChange}
                accept="image/*"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateContest}>
            Create Contest
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Contest Modal */}
      {editFormData && (
        <Modal show={editModal} onHide={() => setEditModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Edit Contest</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Contest Title</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={editFormData.title}
                  onChange={handleEditInputChange}
                  placeholder="Enter contest title"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  value={editFormData.description}
                  onChange={handleEditInputChange}
                  rows={3}
                  placeholder="Enter contest description"
                />
              </Form.Group>

              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      value={editFormData.startDate}
                      onChange={handleEditInputChange}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Start Time</Form.Label>
                    <Form.Control
                      type="time"
                      name="startTime"
                      value={editFormData.startTime}
                      onChange={handleEditInputChange}
                    />
                  </Form.Group>
                </div>
              </div>

              <div className="mb-3">
                <Form.Label>Submissions End / Voting Starts</Form.Label>
                <div className="row">
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Submissions End Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="submissionEndDate"
                        value={editFormData.submissionEndDate}
                        onChange={handleEditInputChange}
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Submissions End Time</Form.Label>
                      <Form.Control
                        type="time"
                        name="submissionEndTime"
                        value={editFormData.submissionEndTime}
                        onChange={handleEditInputChange}
                      />
                    </Form.Group>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <Form.Label>Contest End</Form.Label>
                <div className="row">
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Contest End Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="contestEndDate"
                        value={editFormData.contestEndDate}
                        onChange={handleEditInputChange}
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Contest End Time</Form.Label>
                      <Form.Control
                        type="time"
                        name="contestEndTime"
                        value={editFormData.contestEndTime}
                        onChange={handleEditInputChange}
                      />
                    </Form.Group>
                  </div>
                </div>
              </div>
              {/* Optionally include an image field if needed */}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleUpdateContest}>
              Update Contest
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
